/**
 * Other-traffic symbology (pure). Traffic is context: small faint arrows that fade with age and
 * are carried forward briefly along their track between the 5–10 s pictures, so they glide
 * instead of jumping — never far enough to pretend to be a live position.
 */
import type { EpochMs, LatLon, TrafficTarget } from '@apos/shared';
import { destination, KT_TO_MPS } from './geometry';

/** "N123AB" / "SIM172" / "A1B2C3" — the best short name a crew member would recognise. */
export function trafficName(t: Pick<TrafficTarget, 'registration' | 'callsign' | 'hex'>): string {
  return t.registration?.trim() || t.callsign?.trim() || t.hex.replace(/^~/, '').toUpperCase();
}

/** Never dead-reckon a traffic target further ahead than this (s). */
export const TRAFFIC_MAX_EXTRAP_SEC = 12;

/** Position at `now`, extrapolated along track for at most TRAFFIC_MAX_EXTRAP_SEC. */
export function trafficPosition(t: Pick<TrafficTarget, 'lat' | 'lon' | 't' | 'gsKt' | 'trackDeg'>, now: EpochMs): LatLon {
  const ahead = Math.min(TRAFFIC_MAX_EXTRAP_SEC, Math.max(0, (now - t.t) / 1000));
  if (!ahead || !t.gsKt || t.gsKt < 1 || t.trackDeg === null || !Number.isFinite(t.trackDeg)) return { lat: t.lat, lon: t.lon };
  return destination(t.lat, t.lon, t.trackDeg, t.gsKt * KT_TO_MPS * ahead);
}

/** Icon opacity: faint when fresh, fainter as the position ages toward the drop age. */
export function trafficOpacity(ageSec: number): number {
  if (ageSec <= 15) return 0.8;
  if (ageSec >= 60) return 0.3;
  return Math.round((0.8 - ((ageSec - 15) / 45) * 0.5) * 100) / 100;
}

/** "N123AB · 3,500 ft" — name and altitude, the two things worth a glance. */
export function trafficLabel(name: string, altFt: number | null, formatAlt: (ft: number | null) => string): string {
  if (altFt === null) return name;
  return `${name} · ${formatAlt(Math.round(altFt / 100) * 100)}`;
}
